import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
  ListItemAvatar,
  Avatar,
  Snackbar,
  Alert,
  useTheme,
  Typography,
} from "@mui/material";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import useUpdate from "../../../../api/useUpdate";
import { useTranslation } from "react-i18next";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useLocation } from "react-router-dom";

const EditExpenseDialog = ({ open, onClose, expense }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const theme = useTheme();
  const location = useLocation();
  const pathname = location.pathname;

  const [formData, setFormData] = useState({
    amount: "",
    expenseType: "",
    expenseSource: "",
    description: "",
    date: "",
    time: "",
    companyName: "",
    invoiceNumber: "",
    files: [],
    removeFiles: [],
  });

  const [isExpenseTypeDisabled, setIsExpenseTypeDisabled] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const { data: expenseTypes } = useFetchObjects(
    ["expense-types"],
    "expense-types/",
    token
  );

  const { data: expenseSource } = useFetchObjects(
    ["expense-sources"],
    "expense-sources/",
    token
  );

  const { handleUpdate, loading, success } = useUpdate("expenses", token);

  useEffect(() => {
    if (expense) {
      const disableExpenseType = pathname === "/reception/expenses";
      setIsExpenseTypeDisabled(disableExpenseType);

      setFormData({
        amount: expense.amount || "",
        expenseType: disableExpenseType ? "18" : expense.expenseType || "",
        expenseSource: expense.expenseSource || "",
        description: expense.description || "",
        date: expense.date || "",
        time: expense.time || "",
        companyName: expense.company_name || "",
        invoiceNumber: expense.invoice_number || "",
        files: expense.files || [],
        removeFiles: [],
      });
    }
  }, [expense, pathname]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files) {
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];

      const validFiles = Array.from(files).filter((file) =>
        allowedTypes.includes(file.type)
      );

      if (validFiles.length !== files.length) {
        setSnackbarMessage(
          t("finance.invalidFileType", {
            defaultValue:
              "Invalid file type. Please upload images, PDFs, or Word documents.",
          })
        );
        setSnackbarOpen(true);
      }

      setFormData((prevData) => ({
        ...prevData,
        files: [...prevData.files, ...validFiles],
      }));
    } else {
      if (name === "expenseType" && isExpenseTypeDisabled) return;

      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleRemoveFile = (index) => {
    const newFiles = [...formData.files];
    const fileId = newFiles[index]?.id;

    setFormData((prevData) => ({
      ...prevData,
      removeFiles: fileId
        ? [...prevData.removeFiles, fileId]
        : prevData.removeFiles,
      files: newFiles.filter((_, i) => i !== index),
    }));
  };

  const handleSave = () => {
    const updatedData = new FormData();
    updatedData.append("amount", formData.amount);
    updatedData.append("expenseType", formData.expenseType);
    updatedData.append("expenseSource", formData.expenseSource);
    updatedData.append("description", formData.description);
    updatedData.append("date", formData.date);
    updatedData.append("time", formData.time);
    updatedData.append("company_name", formData.companyName);
    updatedData.append("invoice_number", formData.invoiceNumber);

    formData.files.forEach((file) => {
      if (file instanceof File) {
        updatedData.append("files", file);
      }
    });

    if (formData.removeFiles.length > 0) {
      formData.removeFiles.forEach((fileId) => {
        updatedData.append("remove_files", fileId);
      });
    }

    handleUpdate(expense.id, updatedData);
  };

  useEffect(() => {
    if (success) {
      onClose();
    }
  }, [success, onClose]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        }}
      >
        {t("finance.editExpense", "Edit Expense")}
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          margin="normal"
          label={t("finance.amount", "Amount")}
          name="amount"
          type="number"
          value={formData.amount}
          onChange={handleChange}
          InputProps={{ sx: { borderRadius: 1 } }}
        />

        <FormControl
          fullWidth
          margin="normal"
          sx={{ borderRadius: 1 }}
          disabled={isExpenseTypeDisabled}
        >
          <InputLabel>{t("finance.expenseType", "Expense Type")}</InputLabel>
          <Select
            name="expenseType"
            value={formData.expenseType}
            onChange={handleChange}
            label={t("finance.expenseType", "Expense Type")}
            sx={{ borderRadius: 1 }}
            disabled={isExpenseTypeDisabled} // <-- Explicit disable on Select
          >
            {expenseTypes?.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal" sx={{ borderRadius: 1 }}>
          <InputLabel>
            {t("finance.expenseSource", "Expense Source")}
          </InputLabel>
          <Select
            name="expenseSource"
            value={formData.expenseSource}
            onChange={handleChange}
            label={t("finance.expenseSource", "Expense Source")}
            sx={{ borderRadius: 1 }}
          >
            {expenseSource?.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Company Name Field */}
        <TextField
          fullWidth
          margin="normal"
          label={t("finance.companyName", "Company Name")}
          name="companyName"
          value={formData.companyName}
          onChange={handleChange}
          InputProps={{ sx: { borderRadius: 1 } }}
        />

        {/* Invoice Number Field */}
        <TextField
          fullWidth
          margin="normal"
          label={t("finance.invoiceNumber", "Invoice Number")}
          name="invoiceNumber"
          value={formData.invoiceNumber}
          onChange={handleChange}
          InputProps={{ sx: { borderRadius: 1 } }}
        />

        <TextField
          fullWidth
          margin="normal"
          label={t("finance.description", "Description")}
          name="description"
          value={formData.description}
          onChange={handleChange}
          multiline
          rows={4}
          InputProps={{ sx: { borderRadius: 1 } }}
        />

        <TextField
          fullWidth
          margin="normal"
          label={t("finance.date", "Date")}
          name="date"
          type="date"
          value={formData.date}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          InputProps={{ sx: { borderRadius: 1 } }}
        />

        <TextField
          fullWidth
          margin="normal"
          label={t("finance.time", "Time")}
          name="time"
          type="time"
          value={formData.time}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          InputProps={{ sx: { borderRadius: 1 } }}
        />

        {/* File List */}
        {formData.files.length > 0 && (
          <>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              {t("finance.selectedFiles", "Selected Files")}
            </Typography>
            <List dense sx={{ maxHeight: 200, overflowY: "auto" }}>
              {formData.files.map((file, index) => (
                <ListItem
                  key={index}
                  sx={{
                    bgcolor: theme.palette.background.paper,
                    borderRadius: 1,
                    mb: 1,
                  }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleRemoveFile(index)}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.grey[200],
                        color: theme.palette.text.secondary,
                      }}
                    >
                      <InsertDriveFileIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={file.name || file.filename || "Unknown file"}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}

        <TextField
          fullWidth
          margin="normal"
          type="file"
          name="files"
          onChange={handleChange}
          inputProps={{ multiple: true }}
          sx={{
            mt: 2,
            "& input": {
              cursor: "pointer",
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          color="secondary"
          sx={{ textTransform: "none" }}
        >
          {t("finance.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={loading}
          sx={{ textTransform: "none" }}
        >
          {loading
            ? t("finance.saving", "Saving...")
            : t("finance.save", "Save")}
        </Button>
      </DialogActions>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{
            bgcolor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default EditExpenseDialog;
